import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../utils/api';

const DEFAULT_LOGO = '/caaoc-logo.svg';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale, t, prefix } = useLanguage();

  useEffect(() => {
    api.get<{ config: Record<string, string> }>('/config')
      .then((data) => {
        if (data.config?.site_logo_url) {
          setLogoUrl(data.config.site_logo_url);
        }
      })
      .catch(() => {});
  }, []);

  const navLinks = [
    { labelKey: 'nav.gala', path: '/gala' },
    { labelKey: 'nav.events', path: '/events' },
    { labelKey: 'nav.news', path: '/news' },
    { labelKey: 'nav.schools', path: '/schools' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    if (locale === 'en') {
      // Switch to Chinese - navigate to /zh prefixed route
      const currentPath = location.pathname;
      setLocale('zh');
      navigate('/zh' + (currentPath === '/' ? '' : currentPath));
    } else {
      // Switch to English - remove /zh prefix
      const currentPath = location.pathname.replace(/^\/zh/, '') || '/';
      setLocale('en');
      navigate(currentPath);
    }
  };

  const isActive = (path: string) => {
    const fullPath = prefix + path;
    return location.pathname === fullPath;
  };

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: 'var(--white)',
        boxShadow: scrolled ? 'var(--shadow-md)' : 'none',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <div
        className="container"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 72,
        }}
      >
        {/* Logo */}
        <Link to={prefix + '/'} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={logoUrl}
            alt="CAAOC"
            style={{
              height: 48,
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.1, color: 'var(--gray-900)' }}>
              CAAOC
            </div>
            <div style={{ fontSize: '0.65rem', color: 'var(--gray-500)', letterSpacing: 0.5 }}>
              {locale === 'zh' ? 'Chinese-American Association of OC' : '橙县华人协会'}
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 32,
          }}
          className="desktop-nav"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={prefix + link.path}
              style={{
                fontSize: '0.95rem',
                fontWeight: 500,
                color: isActive(link.path) ? 'var(--red-primary)' : 'var(--gray-700)',
                transition: 'color 0.2s',
              }}
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={toggleLanguage}
            style={{
              background: 'none',
              border: '1px solid var(--gray-300)',
              borderRadius: 'var(--radius-sm)',
              padding: '6px 12px',
              fontSize: '0.85rem',
              color: 'var(--gray-600)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            className="lang-toggle"
          >
            {locale === 'zh' ? 'English' : '中文'}
          </button>
          <Link
            to={prefix + '/events'}
            className="btn btn-primary events-cta"
            style={{ padding: '8px 20px', fontSize: '0.9rem' }}
          >
            {t('nav.events')}
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="hamburger-btn"
            aria-label="Toggle menu"
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              padding: 4,
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <span
              style={{
                display: 'block',
                width: 24,
                height: 2,
                background: 'var(--gray-700)',
                borderRadius: 2,
                transition: 'all 0.3s',
                transform: mobileOpen ? 'rotate(45deg) translateY(7px)' : 'none',
              }}
            />
            <span
              style={{
                display: 'block',
                width: 24,
                height: 2,
                background: 'var(--gray-700)',
                borderRadius: 2,
                transition: 'all 0.3s',
                opacity: mobileOpen ? 0 : 1,
              }}
            />
            <span
              style={{
                display: 'block',
                width: 24,
                height: 2,
                background: 'var(--gray-700)',
                borderRadius: 2,
                transition: 'all 0.3s',
                transform: mobileOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div
          className="mobile-nav-overlay"
          style={{
            position: 'fixed',
            top: 72,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--white)',
            zIndex: 999,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={prefix + link.path}
              style={{
                fontSize: '1.15rem',
                fontWeight: 600,
                color: isActive(link.path) ? 'var(--red-primary)' : 'var(--gray-800)',
                padding: '12px 0',
                borderBottom: '1px solid var(--gray-200)',
              }}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <button
            onClick={toggleLanguage}
            style={{
              marginTop: 8,
              padding: '12px 0',
              borderBottom: '1px solid var(--gray-200)',
              background: 'none',
              border: 'none',
              fontSize: '1.15rem',
              fontWeight: 600,
              color: 'var(--gray-800)',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {locale === 'zh' ? 'English' : '中文'}
          </button>
          <Link
            to={prefix + '/events'}
            className="btn btn-primary"
            style={{ marginTop: 16, justifyContent: 'center' }}
          >
            {t('nav.events')}
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .events-cta { display: none !important; }
          .lang-toggle { display: none !important; }
          .hamburger-btn { display: flex !important; }
        }
      `}</style>
    </header>
  );
}
